import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  styled,
  tableCellClasses,
} from '@mui/material';
import { useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import useTranslatedTransactionStatuses from '@/hooks/useTranslatedTransactionStatuses';

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.root}`]: {
    borderBottomWidth: '0.5px',
    borderBottomColor: 'rgb(224, 224, 224)',
  },
}));

const TransferACHInformation = ({
  transferId,
  destinationBankAccount,
  sourceAchDetails,
  destinationAchDetails,
}) => {
  const [copied, setCopied] = useState(false);
  const { getTranslatedStatus } = useTranslatedTransactionStatuses();

  const handleCopy = () => {
    navigator.clipboard.writeText(transferId);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 5000);
  };

  return (
    <Box>
      {transferId && (
        <>
          <Typography sx={{ mb: 1.5 }} color='text.secondary'>
            Transfer ID
          </Typography>
          <Box
            sx={{ mb: 1.5 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}>
            <Typography color='text.primary' fontSize={14}>
              {transferId}
            </Typography>
            <Button
              variant='outlined'
              startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
              onClick={handleCopy}>
              {copied ? 'Copied!' : 'Click to Copy'}
            </Button>
          </Box>
        </>
      )}
      {destinationBankAccount && (
        <>
          <Typography sx={{ mb: 1.5, mt: 3 }} color='text.secondary'>
            Destination Bank Details
          </Typography>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label='simple table'>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Holder Name</StyledTableCell>
                  <StyledTableCell align='right'>Bank Name</StyledTableCell>
                  <StyledTableCell align='right'>
                    Bank Account Type
                  </StyledTableCell>
                  <StyledTableCell align='right'>
                    Last 4 account digits
                  </StyledTableCell>
                  <StyledTableCell align='right'>
                    Routing Number
                  </StyledTableCell>
                  <StyledTableCell align='right'></StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow
                  sx={{
                    '&:last-child td, &:last-child th': {
                      border: 0,
                    },
                  }}>
                  <StyledTableCell component='th' scope='row'>
                    {destinationBankAccount?.holderName ?? 'N/A'}
                  </StyledTableCell>
                  <StyledTableCell align='right'>
                    {destinationBankAccount?.bankName ?? 'N/A'}
                  </StyledTableCell>
                  <StyledTableCell align='right'>
                    {destinationBankAccount?.bankAccountType ?? 'N/A'}
                  </StyledTableCell>
                  <StyledTableCell align='right'>
                    {destinationBankAccount?.lastFourAccountNumber
                      ? `...${destinationBankAccount?.lastFourAccountNumber}`
                      : 'N/A'}
                  </StyledTableCell>
                  <StyledTableCell align='right'>
                    {destinationBankAccount?.routingNumber ?? 'N/A'}
                  </StyledTableCell>
                  <StyledTableCell align='right'></StyledTableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
        </>
      )}
      <Typography sx={{ mb: 1.5, mt: 3 }} color='text.secondary'>
        Source Ach Details
      </Typography>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label='simple table'>
          <TableHead>
            <TableRow>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell align='left'>Trace Number</StyledTableCell>
              <StyledTableCell align='center'>Return Code</StyledTableCell>
              <StyledTableCell align='left'>Return Reason</StyledTableCell>
              <StyledTableCell align='left'>Return Description</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow
              sx={{
                '&:last-child td, &:last-child th': {
                  border: 0,
                },
              }}>
              <StyledTableCell component='th' scope='row'>
                {sourceAchDetails?.status
                  ? getTranslatedStatus(sourceAchDetails.status)
                  : 'N/A'}
              </StyledTableCell>
              <StyledTableCell align='left'>
                {sourceAchDetails?.traceNumber ?? 'N/A'}
              </StyledTableCell>
              <StyledTableCell align='center'>
                <Chip
                  label={sourceAchDetails?.return?.code ?? 'N/A'}
                  variant='outlined'
                  color={sourceAchDetails?.return?.code ? 'error' : 'default'}
                />
              </StyledTableCell>
              <StyledTableCell align='left'>
                {sourceAchDetails?.return?.reason ?? 'N/A'}
              </StyledTableCell>
              <StyledTableCell align='left'>
                {sourceAchDetails?.return?.description ?? 'N/A'}
              </StyledTableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Typography sx={{ mb: 1.5, mt: 3 }} color='text.secondary'>
        Destination Ach Details
      </Typography>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label='simple table'>
          <TableHead>
            <TableRow>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell align='left'>Trace Number</StyledTableCell>
              <StyledTableCell align='center'>Return Code</StyledTableCell>
              <StyledTableCell align='left'>Return Reason</StyledTableCell>
              <StyledTableCell align='left'>Return Description</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow
              sx={{
                '&:last-child td, &:last-child th': {
                  border: 0,
                },
              }}>
              <StyledTableCell component='th' scope='row'>
                {destinationAchDetails?.status
                  ? getTranslatedStatus(destinationAchDetails.status)
                  : 'N/A'}
              </StyledTableCell>
              <StyledTableCell align='left'>
                {destinationAchDetails?.traceNumber ?? 'N/A'}
              </StyledTableCell>
              <StyledTableCell align='center'>
                <Chip
                  label={destinationAchDetails?.return?.code ?? 'N/A'}
                  variant='outlined'
                  color={
                    destinationAchDetails?.return?.code ? 'error' : 'default'
                  }
                />
              </StyledTableCell>
              <StyledTableCell align='left'>
                {destinationAchDetails?.return?.reason ?? 'N/A'}
              </StyledTableCell>
              <StyledTableCell align='left'>
                {destinationAchDetails?.return?.description ?? 'N/A'}
              </StyledTableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TransferACHInformation;
